import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, ArrowRight, MapPin, Factory, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';

const IndustrialEPCDorset = () => {
  const towns = ['Dorchester', 'Weymouth', 'Poole', 'Bournemouth', 'Blandford Forum', 'Bridport', 'Sherborne', 'Wimborne', 'Wareham', 'Shaftesbury'];
  const industrialEstates = [
    { name: 'Nuffield Industrial Estate', location: 'Poole', desc: 'Major trading estate serving the Poole and Bournemouth conurbation' },
    { name: 'Ferndown Industrial Estate', location: 'Ferndown', desc: 'Large industrial park near Bournemouth Airport' },
    { name: 'Granby Industrial Estate', location: 'Weymouth', desc: 'Established industrial estate serving South Dorset' },
    { name: 'Poundbury Industrial', location: 'Dorchester', desc: 'Modern business units in the Poundbury development' },
    { name: 'Holton Heath Industrial', location: 'Wareham', desc: 'Trading estate with rail access near Poole Harbour' }
  ];

  const localSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Industrial EPC Assessments Dorset",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Energy Performance Direct",
      "areaServed": { "@type": "State", "name": "Dorset" }
    },
    "areaServed": { "@type": "State", "name": "Dorset" },
    "serviceType": "Industrial Energy Performance Certificate"
  };

  return (
    <>
      <Helmet>
        <title>Industrial EPC Dorset | Warehouse & Factory EPCs Poole, Dorchester</title>
        <meta name="description" content="Industrial EPC assessments across Dorset including Poole, Dorchester, Weymouth and Bournemouth. Professional warehouse and factory EPC surveys for Dorset industrial properties." />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/industrial-epc-dorset" />
        <script type="application/ld+json">{JSON.stringify(localSchema)}</script>
      </Helmet>
      
      <Navbar />
      <Breadcrumbs items={[{ label: 'Industrial EPC', path: '/industrial-epc' }, { label: 'Dorset', path: '/industrial-epc-dorset' }]} />
      
      <section className="bg-gradient-to-br from-indigo-800 via-indigo-700 to-indigo-800 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-indigo-500/20 text-indigo-200 px-4 py-2 rounded-full text-sm mb-6">
              <MapPin className="w-4 h-4" /> Dorset
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Industrial EPC Assessments in Dorset</h1>
            <p className="text-xl text-indigo-200 mb-8">Professional warehouse, factory and workshop EPC surveys across Poole, Dorchester, Weymouth and throughout Dorset.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-white text-indigo-700 hover:bg-gray-100">
                <Link to="/booking?service=commercial_warehouse">Get Dorset EPC Quote <ArrowRight className="ml-2 w-5 h-5" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <a href="tel:01392980077"><Phone className="mr-2 w-5 h-5" />Call 01392 980077</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Industrial EPC Services Across Dorset</h2>
            <p className="text-gray-700">
              Dorset's industrial economy ranges from marine engineering around Poole Harbour to food processing and distribution throughout the county. Energy Performance Direct provides professional <Link to="/industrial-epc" className="text-indigo-600 hover:underline">industrial EPC assessments</Link> across Dorset for landlords, developers and business owners requiring compliant energy certificates.
            </p>
            <p className="text-gray-700">
              Our accredited non-domestic energy assessors cover all major Dorset towns including Dorchester, Weymouth, Poole, Bournemouth, Blandford Forum, Bridport, Sherborne, Wimborne, Wareham and Shaftesbury. We regularly assess industrial properties on established trading estates around Poole and the growing business parks throughout the county.
            </p>
            <p className="text-gray-700">
              Dorset's coastal location means some industrial buildings face specific challenges including salt exposure and humidity. Our assessors understand these local factors when assessing building fabric and systems, providing accurate certificates with practical improvement recommendations.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">Dorset Industrial Areas We Cover</h2>
            <div className="not-prose grid md:grid-cols-2 gap-4">
              {industrialEstates.map((estate, i) => (
                <div key={i} className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900">{estate.name}</h3>
                  <p className="text-sm text-indigo-600 mb-2">{estate.location}</p>
                  <p className="text-sm text-gray-600">{estate.desc}</p>
                </div>
              ))}
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">Towns Covered in Dorset</h2>
            <p className="text-gray-700">We provide industrial EPC assessments throughout Dorset including:</p>
            <div className="not-prose flex flex-wrap gap-2 mt-4">
              {towns.map((town, i) => (
                <span key={i} className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm">{town}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Services</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <Link to="/industrial-epc" className="p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                <Factory className="w-6 h-6 text-indigo-600 mb-2" />
                <h3 className="font-semibold">Industrial EPCs</h3>
                <p className="text-sm text-gray-600">All industrial building types</p>
              </Link>
              <Link to="/warehouse-epc" className="p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                <Building2 className="w-6 h-6 text-indigo-600 mb-2" />
                <h3 className="font-semibold">Warehouse EPCs</h3>
                <p className="text-sm text-gray-600">Distribution and storage</p>
              </Link>
              <Link to="/commercial-epc-dorset" className="p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                <Building2 className="w-6 h-6 text-indigo-600 mb-2" />
                <h3 className="font-semibold">Commercial EPCs Dorset</h3>
                <p className="text-sm text-gray-600">All commercial buildings</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Get Your Dorset Industrial EPC Quote</h2>
          <p className="text-xl text-green-100 mb-8">Professional assessments throughout Dorset.</p>
          <Button asChild size="lg" className="bg-white text-green-600 hover:bg-gray-100">
            <Link to="/booking?service=commercial_warehouse">Request Quote <ArrowRight className="ml-2 w-5 h-5" /></Link>
          </Button>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default IndustrialEPCDorset;